import { QueryClientContext } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import { DestinationQueryKeys } from 'src/hooks/useDestination';

/**
 * NPT-2053 — the `proximity` hint for location search, as Mapbox's "lng,lat".
 *
 * - `destination` (where to stay): the org's area first, its first popular destination (admins
 *   order them, and pick them around the org's events), then the traveler's own position.
 * - `address` (the traveler's own billing/home address): the traveler's position only. Anchoring
 *   on the org would pull street suggestions toward the venue's city, not where they live.
 *
 * The traveler's position is used ONLY when the browser already granted geolocation: location
 * search never triggers a permission prompt. Like useMarket, nothing here fetches or needs a
 * provider: the org comes from redux and its destinations from the react-query cache that
 * HomeFilter / MapViewSearch already fill; absent either, the anchor is simply skipped.
 */
export type ProximityMode = 'destination' | 'address';

/** Two decimals (~1 km): plenty for ranking, and keeps the query key stable as a device drifts. */
export const toProximity = (lng: unknown, lat: unknown): string | undefined => {
   const x = Number(lng);
   const y = Number(lat);
   if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(x) > 180 || Math.abs(y) > 90) return undefined;
   if (x === 0 && y === 0) return undefined;
   return `${x.toFixed(2)},${y.toFixed(2)}`;
};

/** The first popular destination that carries a usable center ([lng, lat]). */
export const proximityFromDestinations = (destinations: unknown): string | undefined => {
   if (!Array.isArray(destinations)) return undefined;
   for (const destination of destinations) {
      const center = destination?.mapboxObject?.center;
      if (Array.isArray(center) && center.length >= 2) {
         const proximity = toProximity(center[0], center[1]);
         if (proximity) return proximity;
      }
   }
   return undefined;
};

let grantedPosition: Promise<string | undefined> | null = null;

/** The traveler's position when geolocation is already granted; never prompts. Shared per page load. */
export const getGrantedPosition = (): Promise<string | undefined> => {
   if (grantedPosition) return grantedPosition;
   grantedPosition = (async () => {
      try {
         if (typeof navigator === 'undefined' || !navigator.geolocation || !navigator.permissions?.query) return undefined;
         const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
         if (status.state !== 'granted') return undefined;
         return await new Promise<string | undefined>(resolve => {
            navigator.geolocation.getCurrentPosition(
               pos => resolve(toProximity(pos.coords.longitude, pos.coords.latitude)),
               () => resolve(undefined),
               { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout: 3000 },
            );
         });
      } catch {
         return undefined;
      }
   })();
   // A denial or failure is not cached for good: permission can be granted later in the session.
   grantedPosition.then(position => {
      if (!position) grantedPosition = null;
   });
   return grantedPosition;
};

/** Test seam: forget the cached position. */
export const resetGrantedPositionForTests = () => {
   grantedPosition = null;
};

const noopUnsubscribe = () => {};

const useOrgId = (): string | undefined => {
   const redux = useContext(ReactReduxContext);
   const store = redux?.store;
   const subscribe = useCallback((onChange: () => void) => (store ? store.subscribe(onChange) : noopUnsubscribe), [store]);
   const getSnapshot = () => (store?.getState() as { org?: { organization?: { id?: string } | null } } | undefined)?.org?.organization?.id;
   return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const useOrgAnchor = (enabled: boolean): string | undefined => {
   const orgId = useOrgId();
   const client = useContext(QueryClientContext);
   const active = enabled && !!client && !!orgId;
   const subscribe = useCallback(
      (onChange: () => void) => (active && client ? client.getQueryCache().subscribe(onChange) : noopUnsubscribe),
      [active, client],
   );
   const getSnapshot = () =>
      active
         ? proximityFromDestinations(
              client!.getQueryData<{ data?: unknown }>([DestinationQueryKeys.GetTravelerDestinations, { page: 0, limit: -1 }, orgId])?.data,
           )
         : undefined;
   return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const useSearchProximity = (mode: ProximityMode = 'destination'): string | undefined => {
   const orgAnchor = useOrgAnchor(mode === 'destination');
   const [position, setPosition] = useState<string | undefined>(undefined);

   useEffect(() => {
      let live = true;
      getGrantedPosition().then(p => {
         if (live) setPosition(p);
      });
      return () => {
         live = false;
      };
   }, []);

   return mode === 'destination' ? (orgAnchor ?? position) : position;
};
